import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';
import { Shield, Camera, Filter, AlertTriangle, ListChecks } from 'lucide-react';

/**
 * Help modal for the Capture Profiles settings tab. Walks the user
 * through what profiles do, the three capture modes, how a pattern
 * matches (the most specific one wins, as `matchCaptureProfile` in
 * @vd/redaction decides - this modal said the opposite until 2026-09-28),
 * what's always redacted by default, and a few concrete examples.
 *
 * Scoped narrow on purpose: every claim here mirrors behavior in
 * `apps/api/src/captureRedaction.ts`. If that file's behavior changes,
 * update this modal in lockstep.
 */
export function CaptureProfilesHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} width={680}>
      <ModalHeader>How capture profiles work</ModalHeader>
      <ModalBody>
        {/* Quick-reference card. Same content the page used to show
            inline; moved into the help modal per product feedback so
            the tab itself stays compact. */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 6,
          padding: '12px 14px', marginBottom: 24,
          border: '1px solid var(--border)', borderRadius: 6,
          background: 'var(--bg-subtle)', fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <div><strong style={{ color: 'var(--text)' }}>Three modes:</strong> Full capture · Metadata only · Never capture.</div>
          <div><strong style={{ color: 'var(--text)' }}>Pattern matching:</strong> a <code>*</code> at the end covers everything below it. <em>The most specific pattern wins</em>, so a narrow rule overrides a broad one.</div>
          <div><strong style={{ color: 'var(--text)' }}>Always redacted:</strong> auth headers, common token / key URL params, password / token JSON keys. Regardless of profile.</div>
        </div>

        <Section
          icon={<Camera size={16} />}
          title="What capture profiles are"
        >
          <p>
            A capture profile is a rule that says <em>"when the extension
            captures a page on origin X, capture this much and redact
            these extras."</em> Profiles let you carve out different
            behavior for different parts of your stack. Capture
            everything on a public marketing site, capture metadata only
            on an internal admin tool, capture nothing on a partner
            portal you can't audit.
          </p>
          <p style={{ marginTop: 8 }}>
            Without any profiles, every capture defaults to <strong>full
            capture</strong>. Profiles are subtractive: they let you
            opt OUT of capturing things on specific origins.
          </p>
        </Section>

        <Section
          icon={<ListChecks size={16} />}
          title="The three capture modes"
        >
          <ModeBlock
            label="Full capture"
            color="var(--success)"
            desc={
              <>
                Screenshot + console + network (with request/response
                bodies, up to size cap) + JS errors + performance.
                Default for any origin without a profile.
              </>
            }
          />
          <ModeBlock
            label="Metadata only"
            color="var(--warning)"
            desc={
              <>
                Screenshot + console + network (URLs, methods, status
                codes, durations) + errors + performance. <strong>No
                request/response bodies.</strong> Use for sensitive APIs
                where you want the timing/error picture but not the
                payloads.
              </>
            }
          />
          <ModeBlock
            label="Never capture"
            color="var(--danger)"
            desc={
              <>
                The extension does nothing on URLs matching this
                pattern. Use for anything truly off-limits, like banking
                tabs, an HR app, or customer PII screens.
              </>
            }
          />
        </Section>

        <Section
          icon={<Filter size={16} />}
          title={'How matching works (read carefully)'}
        >
          <p>
            A pattern ending in <code>*</code> covers every address that
            starts with it, at any depth:
            <code style={{ marginLeft: 4 }}>https://acme.com/admin/*</code> covers
            <code> https://acme.com/admin/users/42</code>. A pattern with no
            <code> *</code> covers only that exact address, so
            <code> https://acme.com</code> on its own is the home page and
            nothing else. The editor's <em>Cover the whole site</em> option
            saves <code>https://acme.com/*</code> for you.
          </p>
          <p style={{ marginTop: 8 }}>
            A <code>*</code> only works at the end. <code>https://*.acme.com/*</code> is
            refused, because it would match nothing: add a profile for each
            subdomain instead. <code>/**</code> is accepted and saved as <code>/*</code>.
          </p>
          <Callout variant="info">
            <strong>The most specific pattern wins.</strong> When several
            profiles cover a page, the one with the longest pattern applies,
            and between two of the same length the stricter mode wins. So you
            can set <code style={{ marginLeft: 4 }}>https://acme.com/*</code> to
            <em> metadata only</em> and <code>https://acme.com/admin/*</code> to
            <em> never</em>: admin pages are never captured, and the rest of
            the site keeps metadata.
          </Callout>
        </Section>

        <Section
          icon={<Shield size={16} />}
          title="What's always redacted (regardless of profile)"
        >
          <p>
            Every capture (full or metadata) gets the same baseline
            redaction applied server-side. Profiles can ADD to this
            list but never subtract from it.
          </p>
          <RedactionGroup
            label="Headers"
            items={[
              'Authorization', 'Proxy-Authorization', 'Cookie', 'Set-Cookie',
              'X-API-Key', 'X-Auth-Token', 'X-Access-Token', 'X-Session-ID',
              'X-CSRF-Token', 'X-XSRF-Token', 'X-Forwarded-Authorization',
              'X-Amz-Security-Token', 'X-Goog-API-Key',
              'Stripe-Signature', 'Webhook-Signature', 'X-Hub-Signature-256',
            ]}
          />
          <RedactionGroup
            label="URL query params"
            items={[
              'token', 'access_token', 'id_token', 'refresh_token',
              'api_key', 'apikey', 'key', 'secret',
              'password', 'pwd', 'sig', 'signature',
              'code', 'state', 'nonce',
              'session_id', 'sessionid', 'jwt', 'bearer', 'auth',
            ]}
          />
          <RedactionGroup
            label="JSON body keys"
            items={[
              'password', 'newPassword', 'currentPassword', 'pwd',
              'token', 'accessToken', 'idToken', 'refreshToken',
              'apiKey', 'authorization', 'secret', 'client_secret',
              'session', 'session_id', 'jwt', 'bearer', 'code_verifier',
            ]}
          />
        </Section>

        <Section
          icon={<AlertTriangle size={16} />}
          title="Extra redactions per profile"
        >
          <p>
            Use the "Extra header / query-param redactions" field when
            creating a profile to add domain-specific names. For example:
            <code style={{ marginLeft: 4 }}>x-tenant-id</code>,
            <code> x-internal-trace</code>, or a custom auth header
            your team uses. Names are case-insensitive.
          </p>
          <p style={{ marginTop: 8 }}>
            Adding a name here causes the extension <strong>and</strong>
            the API to scrub matching headers + URL params from the
            stored capture. Original values never reach disk.
          </p>
        </Section>

        <Section title="Examples" icon={null}>
          <Example
            pattern="https://app.toado.dev/*"
            mode="Full capture"
            note="Your own app. Capture everything for richest debugging context."
          />
          <Example
            pattern="https://api.stripe.com/*"
            mode="Metadata only"
            note="Capture timing + status codes for rate-limit / 5xx debugging without storing card-related bodies."
          />
          <Example
            pattern="https://online.bank.example.com/*"
            mode="Never capture"
            note="Hard off-limits. The extension captures nothing on this site. Each subdomain the bank uses needs its own profile."
          />
          <Example
            pattern="https://internal-tools.acme.com/*"
            mode="Metadata only"
            extras="x-tenant-id, x-employee-id"
            note="Internal tool that surfaces tenant + employee identifiers in headers. Redact those alongside the defaults."
          />
        </Section>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={onClose}>Got it</Button>
      </ModalFooter>
    </Modal>
  );
}

// ---- Local presentational helpers ----

function Section({ icon, title, children }: { icon: React.ReactNode | null; title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h3 style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 14, margin: '0 0 8px', color: 'var(--text)',
      }}>
        {icon && <span style={{ color: 'var(--text-secondary)' }}>{icon}</span>}
        {title}
      </h3>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
        {children}
      </div>
    </section>
  );
}

function ModeBlock({ label, color, desc }: { label: string; color: string; desc: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', gap: 10, padding: '8px 10px', marginBottom: 6,
      border: '1px solid var(--border)', borderRadius: 6,
      background: 'var(--bg-subtle)',
    }}>
      <div style={{
        width: 4, alignSelf: 'stretch', background: color, borderRadius: 2,
      }} />
      <div>
        <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{label}</div>
        <div style={{ fontSize: 12, marginTop: 2 }}>{desc}</div>
      </div>
    </div>
  );
}

function Callout({ variant, children }: { variant: 'warning' | 'info'; children: React.ReactNode }) {
  const bg = variant === 'warning' ? 'var(--warning-bg, #3a2e0a)' : 'var(--bg-subtle)';
  const border = variant === 'warning' ? 'var(--warning, #d4a73a)' : 'var(--border)';
  return (
    <div style={{
      marginTop: 8, padding: 10,
      border: `1px solid ${border}`,
      borderLeft: `3px solid ${border}`,
      borderRadius: 6,
      background: bg,
      fontSize: 12,
      color: 'var(--text)',
    }}>
      {children}
    </div>
  );
}

function RedactionGroup({ label, items }: { label: string; items: string[] }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {items.map((it) => (
          <code
            key={it}
            style={{
              padding: '2px 6px',
              background: 'var(--bg-muted)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: 11,
              fontFamily: 'ui-monospace, monospace',
              color: 'var(--text-secondary)',
            }}
          >
            {it}
          </code>
        ))}
      </div>
    </div>
  );
}

function Example({ pattern, mode, extras, note }: { pattern: string; mode: string; extras?: string; note: string }) {
  return (
    <div style={{
      marginTop: 8, padding: 10,
      border: '1px solid var(--border)', borderRadius: 6,
      fontSize: 12,
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <code style={{
          padding: '2px 6px', background: 'var(--bg-muted)', borderRadius: 3,
          fontFamily: 'ui-monospace, monospace', color: 'var(--text)',
        }}>
          {pattern}
        </code>
        <span style={{ color: 'var(--text)' }}>→ <strong>{mode}</strong></span>
        {extras && <span style={{ color: 'var(--text-tertiary)' }}>· extras: {extras}</span>}
      </div>
      <div style={{ marginTop: 4, color: 'var(--text-secondary)' }}>{note}</div>
    </div>
  );
}
